import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { PlatformShellHeader, PlatformShellFooter, useSeo } from "./PlatformShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InsightDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let mounted = true;
    setStatus("loading");
    axios.get(`${API}/posts/${slug}`).then((r) => {
      if (!mounted) return;
      setPost(r.data);
      setStatus("ok");
    }).catch((e) => {
      // Log for observability, then surface a friendly missing state.
      console.error("Failed to load insight", slug, e);
      if (mounted) setStatus("missing");
    });
    return () => { mounted = false; };
  }, [slug]);

  /* Sanitise blog HTML to eliminate XSS risk before injecting via dangerouslySetInnerHTML.
     Keeps standard article markup (links, images, headings, code blocks). */
  const safeBodyHtml = useMemo(
    () => (post?.body_html ? DOMPurify.sanitize(post.body_html) : ""),
    [post?.body_html]
  );

  useSeo(post ? {
    title: `${post.title} | Boom Barrier Company`,
    description: post.meta_description,
    canonical: `https://www.boombarriercompany.com/insights/${post.slug}`,
    image: post.hero_image ? `https://www.boombarriercompany.com${post.hero_image}` : undefined,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": post.title,
      "description": post.meta_description,
      "image": post.hero_image ? `https://www.boombarriercompany.com${post.hero_image}` : undefined,
      "datePublished": post.published_at,
      "author": { "@type": "Organization", "name": "Boom Barrier Company" },
      "publisher": { "@type": "Organization", "name": "Boom Barrier Company", "logo": { "@type": "ImageObject", "url": "https://www.boombarriercompany.com/branded/bbc-pro.png" } },
      "keywords": post.meta_keywords,
      "mainEntityOfPage": `https://www.boombarriercompany.com/insights/${post.slug}`,
    },
  } : { title: "Insight — Boom Barrier Company" });

  return (
    <div data-testid="insight-detail-root" className="min-h-screen bg-[#0F1113] text-white font-body antialiased">
      <PlatformShellHeader />

      <article className="relative pt-16 md:pt-24 pb-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link to="/insights" data-testid="insight-back" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] hover:text-[#00FF85] transition mb-10">
            <ArrowLeft size={12} /> All Insights
          </Link>

          {status === "loading" && (
            <div data-testid="insight-loading" className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#71717A]">Loading…</div>
          )}

          {status === "missing" && (
            <div data-testid="insight-missing">
              <h1 className="font-display text-white text-4xl font-black tracking-tight">Insight not found</h1>
              <p className="mt-4 text-[#A1A1AA]">The article you're looking for has moved or doesn't exist yet.</p>
            </div>
          )}

          {status === "ok" && post && (
            <>
              <div className="flex items-center gap-3">
                <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#00FF85] bg-[#00FF85]/10 border border-[#00FF85]/30 px-2.5 py-1">
                  {post.category}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#52525B]">
                  {new Date(post.published_at).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" })}
                </span>
              </div>
              <h1 className="font-display mt-5 text-white text-3xl md:text-5xl font-black tracking-[-0.02em] leading-[1.05]">
                {post.title}
              </h1>
              <p className="mt-5 text-[#B0FFD9] text-base md:text-lg font-body leading-relaxed border-l-2 border-[#00FF85] pl-5">
                {post.excerpt}
              </p>

              {post.hero_image && (
                <div className="mt-10 border border-white/10 bg-[#16191C] aspect-[16/10] overflow-hidden">
                  <img src={post.hero_image} alt={post.title} className="w-full h-full object-cover" />
                </div>
              )}

              <div
                data-testid="insight-body"
                className="mt-12 prose-bbc"
                dangerouslySetInnerHTML={{ __html: safeBodyHtml }}
              />
            </>
          )}
        </div>

        {/* End CTA */}
        <div className="max-w-3xl mx-auto px-6 md:px-12 mt-16">
          <div className="border border-[#00FF85]/30 bg-[#00FF85]/[0.06] px-7 py-7 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#00FF85]">Next Step</div>
              <h3 className="font-display mt-1 text-white text-xl font-bold tracking-tight">Talk to a BBC engineer about your site.</h3>
            </div>
            <Link
              to="/quote"
              data-testid="insight-cta-quote"
              className="inline-flex items-center gap-2 bg-[#00FF85] hover:bg-[#00CC6A] text-black font-semibold px-5 py-3 text-xs uppercase tracking-wider"
            >
              Request a Quote <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </article>

      <PlatformShellFooter />

      {/* tiny inline style for article body */}
      <style>{`
        .prose-bbc h2 { font-family: 'Cabinet Grotesk', 'IBM Plex Sans', sans-serif; color: #FFF; font-weight: 800; font-size: 1.65rem; line-height: 1.15; letter-spacing: -0.02em; margin-top: 2.4rem; margin-bottom: 0.9rem; }
        .prose-bbc h3 { font-family: 'Cabinet Grotesk', sans-serif; color: #FFF; font-weight: 700; font-size: 1.2rem; margin-top: 1.8rem; margin-bottom: 0.6rem; }
        .prose-bbc p { color: #D4D4D8; font-size: 1.025rem; line-height: 1.75; margin-bottom: 1.05rem; }
        .prose-bbc strong { color: #FFFFFF; }
        .prose-bbc em { color: #B0FFD9; font-style: normal; }
        .prose-bbc ul, .prose-bbc ol { color: #D4D4D8; margin: 1rem 0 1.2rem 1.4rem; line-height: 1.7; }
        .prose-bbc li { margin-bottom: 0.55rem; }
        .prose-bbc ul li { list-style-type: disc; }
        .prose-bbc ol li { list-style-type: decimal; }
        .prose-bbc a { color: #00FF85; text-decoration: underline; }
      `}</style>
    </div>
  );
}
